import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO, PROJECTS } from '../data/staticData';
import { getProjects } from '../lib/cmsClient';
import ContactForm from '../components/ContactForm';

export default function ProcessPage() {
  const [projectsList, setProjectsList] = useState(PROJECTS);

  useEffect(() => {
    async function load() {
      const data = await getProjects();
      if (data && data.length > 0) {
        setProjectsList(data);
      }
    }
    load();
  }, []);
  const steps = [
    {
      num: 1,
      title: 'Overvej dine behov',
      desc: 'Tegl, stål eller fibercement? Tagets udseende, holdbarhed og vedligeholdelse varierer – find den løsning, der passer bedst til dit hus.'
    },
    {
      num: 2,
      title: 'Få en idé om prisen',
      desc: 'Brug vores prisberegner for nyt tag for at få et realistisk estimat baseret på dine behov.'
    },
    {
      num: 3,
      title: 'Planlæg dit budget',
      desc: 'Et nyt tag er en investering. Overvej, hvordan du vil finansiere det. En god idé er at tage dit prisestimat med i dialogen med fx. banken'
    },
    {
      num: 4,
      title: 'Få et konkret tilbud',
      desc: 'Når du har styr på rammerne, kan vi give dig et præcist tilbud og lave en plan for dit nye tag. Her kommer vi på besøg - og ser selv tingenes stand'
    },
    {
      num: 5,
      title: 'Klar til nyt tag!',
      desc: 'Vi sørger for en professionel montering, så du får et nyt tag - uden bekymringer.'
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16 border-b border-stone-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-8 space-y-6">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 tracking-tight uppercase">
              Skal din bolig have nyt tag?
            </h1>
            <div className="space-y-4 text-stone-700 text-base sm:text-lg leading-relaxed">
              <p>
                Et nyt tag er en stor beslutning – og en vigtig investering i dit hjem. Vi hos Dansk Tagbyg står altid klar til at hjælpe dig.
              </p>
              <p>
                Vi guider dig gennem processen, så du kan få overblik, tage de rigtige valg og komme godt fra start.
              </p>
              <p className="font-semibold text-stone-900">
                Få overblik over processen og næste skridt for dig.
              </p>
            </div>

            {/* Quick Links */}
            <div className="pt-4 border-t border-stone-200">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">
                Klik og kom længere ned på siden:
              </p>
              <div className="flex flex-wrap gap-4 text-sm font-bold">
                <a href="#processen" className="text-stone-900 hover:text-[#BE0F12] underline">
                  Processen
                </a>
                <span className="text-stone-300">•</span>
                <a href="#hvor-er-du" className="text-stone-900 hover:text-[#BE0F12] underline">
                  Jeg har styr på processen
                </a>
                <span className="text-stone-300">•</span>
                <a href="#kontakt" className="text-stone-900 hover:text-[#BE0F12] underline">
                  Kontakt os
                </a>
              </div>
            </div>
          </div>

          {/* Right Contact Card */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-stone-50 border border-stone-200 p-6 sm:p-8 space-y-4">
              <h3 className="text-lg font-bold text-stone-900 uppercase">
                Kontakt mig, Lasse
              </h3>
              <div className="space-y-2 text-sm text-stone-700">
                <p>
                  <strong>Ring:</strong>{' '}
                  <a href={`tel:${COMPANY_INFO.phone}`} className="text-[#BE0F12] font-semibold hover:underline">
                    {COMPANY_INFO.phoneFormatted}
                  </a>
                </p>
                <p>
                  <strong>Skriv:</strong>{' '}
                  <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#BE0F12] font-semibold hover:underline">
                    {COMPANY_INFO.email}
                  </a>
                </p>
              </div>
              <a
                href="#kontakt"
                className="inline-block w-full text-center px-6 py-3 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-sm tracking-wider uppercase transition-colors"
              >
                Få rådgivning
              </a>
            </div>

            {/* Lasse profilbillede som på originalen */}
            <div className="flex flex-row items-end justify-center gap-2 sm:gap-3 pt-2">
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/har-du-sporgsmaal.png"
                alt="Har du spørgsmål, så tag fat i mig!"
                className="w-[42%] max-w-[145px] h-auto object-contain self-center"
              />
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/lasse-sidebar.png"
                alt="Lasse Vesterhouen - Dansk Tagbyg"
                className="w-[58%] max-w-[200px] h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Steps Section: Sådan kommer du godt i gang med dit nye tag */}
      <section id="processen" className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
              Sådan kommer du godt i gang med dit nye tag
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step) => (
              <div
                key={step.num}
                className="bg-white border border-stone-200 p-6 flex flex-col justify-between space-y-4 shadow-sm"
              >
                <div className="space-y-3">
                  <div className="w-10 h-10 bg-[#BE0F12] text-white font-black text-lg flex items-center justify-center">
                    {step.num}
                  </div>
                  <h3 className="font-extrabold text-stone-900 text-lg">
                    {step.title}
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hvor er du i processen? */}
      <section id="hvor-er-du" className="py-16 sm:py-20 bg-white border-b border-stone-200 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
              Hvor er du i processen?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Kort 1 */}
            <div className="p-8 bg-stone-50 border border-stone-200 flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                  Jeg vil gerne have en idé om prisen
                </h3>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                  Det forstår vi godt! Et nyt tag er en stor beslutning – og det hjælper at kende prisniveauet.
                </p>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                  Med vores prisberegner for nyt tag kan du få et hurtigt estimat baseret på dine valg: tagtype, tagets størrelse og husets form. Det tager kun et par minutter og giver dig et godt udgangspunkt til de næste skridt.
                </p>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed font-semibold">
                  Gør dig klar med ca. m² og en idé om, hvilken tagtype du foretrækker – så er du i gang!
                </p>
              </div>

              <div>
                <Link
                  to="/prisberegner"
                  className="inline-block px-8 py-3.5 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-sm tracking-wider uppercase transition-colors"
                >
                  Prisberegner: Nyt tag
                </Link>
              </div>
            </div>

            {/* Kort 2 */}
            <div className="p-8 bg-stone-50 border border-stone-200 flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                  Jeg har styr på finansiering
                </h3>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                  Fantastisk, at du har styr på finansieringen – det gør næste skridt meget lettere!
                </p>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                  Når du er klar til at få et præcist tilbud, kan vi komme forbi og vurdere dit tag. Vi ser på forholdene, tager mål og diskuterer de bedste løsninger, så vi kan give dig et skræddersyet tilbud, der passer til dit behov og budget.
                </p>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed font-semibold">
                  Udfyld kontaktformularen, og vi aftaler en tid, der passer dig!
                </p>
              </div>

              <div>
                <a
                  href="#kontakt"
                  className="inline-block px-8 py-3.5 bg-stone-900 hover:bg-black text-white font-bold text-sm tracking-wider uppercase transition-colors"
                >
                  Udfyld kontaktformular
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tidligere projekter */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="max-w-3xl space-y-3">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
              Tidligere projekter
            </h2>
            <p className="text-stone-700 text-base leading-relaxed">
              Få inspiration til dit eget tagprojekt ved at se, hvordan vi har hjulpet andre kunder. Her kan du finde eksempler på tagløsninger, der passer til forskellige husstyper og behov.
            </p>
            <p className="text-sm text-stone-600">
              I tvivl om hvilken tagtype, der vil være bedst til dig?{' '}
              <Link to="/viden" className="text-[#BE0F12] font-semibold underline hover:text-black">
                Bliv klogere på de forskellige typer her.
              </Link>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectsList.slice(0, 6).map((proj) => (
              <div key={proj.id} className="bg-white border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between">
                <div className="h-56 bg-stone-100 overflow-hidden">
                  <img
                    src={proj.image}
                    alt={proj.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6 space-y-3">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#BE0F12]">
                    {proj.category}
                  </span>
                  <h3 className="font-extrabold text-stone-900 text-lg">
                    {proj.title}
                  </h3>
                  <div className="text-xs text-stone-600 space-y-1">
                    {proj.price && <p><strong>M² pris:</strong> {proj.price}</p>}
                    {proj.roofType && <p><strong>Tagtype:</strong> {proj.roofType}</p>}
                    {proj.location && <p><strong>Lokation:</strong> {proj.location}</p>}
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <Link
                    to="/projekter"
                    className="text-xs font-bold text-[#BE0F12] uppercase tracking-wider hover:underline"
                  >
                    Se hele projektet →
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center pt-4">
            <Link
              to="/projekter"
              className="inline-block px-8 py-3 bg-stone-900 hover:bg-black text-white font-bold text-sm tracking-wider uppercase transition-colors"
            >
              Se alle projekter
            </Link>
          </div>
        </div>
      </section>

      {/* Kontakt os og få den rette rådgivning */}
      <section className="py-12 bg-white text-center border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 space-y-4">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
            Kontakt os og få den rette rådgivning
          </h2>
          <div>
            <a
              href="#kontakt"
              className="inline-block px-8 py-3.5 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-sm tracking-wider uppercase transition-colors"
            >
              Kontakt os
            </a>
          </div>
        </div>
      </section>

      {/* Bund: Anmeldelser og Kontaktformular (som på originalen) */}
      <section id="kontakt" className="py-16 sm:py-24 bg-stone-100 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 sm:gap-16 items-start">
            
            {/* Venstre kolonne: Firmaoplysninger og Anmeld Håndværker */}
            <div className="lg:col-span-5 space-y-8">
              {/* Anmeld Haandvaerker Badge */}
              <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-3">
                <div className="flex items-center gap-1 text-amber-500 text-lg">
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                </div>
                <a
                  href="https://www.anmeld-haandvaerker.dk/tagdaekker/dansk-tagbyg-aps"
                  target="_blank"
                  rel="noreferrer"
                  className="block font-black text-xl text-stone-900 hover:text-[#BE0F12] transition-colors"
                >
                  4,8 ud af 155 anmeldelser
                </a>
                <p className="text-xs text-stone-500">
                  Finalist til Årets Håndværker 5 år i træk
                </p>
              </div>

              {/* Dansk Tagbyg info */}
              <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-4 text-sm text-stone-700">
                <h3 className="font-extrabold text-xl text-stone-900 uppercase">
                  Dansk Tagbyg ApS
                </h3>
                <div className="space-y-2">
                  <p>
                    <strong className="text-stone-900">Ring:</strong>{' '}
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.phoneFormatted}
                    </a>
                  </p>
                  <p>
                    <strong className="text-stone-900">Skriv:</strong>{' '}
                    <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.email}
                    </a>
                  </p>
                  <p>
                    <strong className="text-stone-900">Adr:</strong> {COMPANY_INFO.address}
                  </p>
                  <p>
                    <strong className="text-stone-900">CVR:</strong> {COMPANY_INFO.cvr}
                  </p>
                </div>
                <div className="pt-3 border-t border-stone-200">
                  <p className="font-bold text-stone-900">Vi løser opgaver i hele DK</p>
                </div>
                <div className="pt-2">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Vi er medlem af</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <img
                      src="/_assets/swisspearl-logo.png"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = 'https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/swisspearl-logo.png';
                      }}
                      alt="Swisspearl"
                      className="h-7 sm:h-8 w-auto object-contain"
                    />
                    <img
                      src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-12-thumb.png"
                      alt="Byg Garanti"
                      className="h-6 w-auto object-contain"
                    />
                    <img
                      src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-11-thumb.png"
                      alt="DI Byggeri"
                      className="h-6 w-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Højre kolonne: Kontaktformular */}
            <div className="lg:col-span-7 bg-white border border-stone-200 p-8 sm:p-10 shadow-sm">
              <ContactForm idPrefix="process" submitButtonText="Send" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
